import {AddressApiDto} from "../../api/model/rest";
import {none, Option, option} from "ts-option";

/**
 * Address model.
 */
export default class Address {

	constructor(public readonly postcode: string,
		public readonly country: string,
		public readonly province: Option<string>,
		public readonly district: Option<string>,
		public readonly city: Option<string>,
		public readonly street: Option<string>,
		public readonly secondaryStreet: Option<string>,
		public readonly buildingName: Option<string>,
		public readonly buildingNumber: Option<string>,
		public readonly subBuilding: Option<string>,
		public readonly company: Option<string>,
		public readonly poBoxNumber: Option<string>,
	) {
	}

	static fromJson(json: AddressApiDto): Address {
		const city = json.city
			? option(json.city)
			: (json.district && json.province)
				? option(`${json.district}, ${json.province}`)
				: option(json.district || json.province);
		return new Address(json.postcode, json.country, option(json.province), option(json.district),
			city, option(json.street), option(json.secondaryStreet), option(json.buildingName),
			option(json.buildingNumber), option(json.subBuilding), option(json.company), option(json.poBoxNumber));
	}

	toJson(): AddressApiDto {
		return {
			postcode: this.postcode,
			country: this.country,
			province: this.province.orNull,
			district: this.district.orNull,
			city: this.city.orNull,
			street: this.street.orNull,
			secondaryStreet: this.secondaryStreet.orNull,
			buildingName: this.buildingName.orNull,
			buildingNumber: this.buildingNumber.orNull,
			subBuilding: this.subBuilding.orNull,
			company: this.company.orNull,
			poBoxNumber: this.poBoxNumber.orNull
		};
	}

	static fromManualInput(buildingName: Option<string>,
		subBuilding: Option<string>,
		buildingNumber: Option<string>,
		street: Option<string>,
		city: Option<string>,
		postcode: string) {
		return new Address(postcode, "Ireland", none, none, city,
			street, none, buildingName, buildingNumber, subBuilding, none, none)
	}

	/**
	 * Gets address as displayable string.
	 * @returns {string} displayable address.
	 */
	displayName(): string {
		const company = this.company.map(c => `${c}, `).getOrElse("");
		const street = this.displayStreet();
		const streetStr = street.length ? street + "\n" : "";
		const cityStr = this.city.map(s => this.postcode ? `${s} (${this.postcode})` : s).getOrElse(this.postcode || "");
		return `${company}${streetStr}${cityStr}`;
	}

	/**
	 * Display street text.
	 */
	displayStreet() {
		const buildingName = this.buildingName.map(bn => `${bn}, `).getOrElse("");
		const subBuildingNumber = this.subBuilding.map(sb => `${sb}, `).getOrElse("");
		const buildingNumber = this.buildingNumber.map(bn => `${bn} `).getOrElse("");
		const poBoxNumber = this.poBoxNumber.map(pbn => `${pbn} `).getOrElse("");
		const street = this.street.getOrElse("");
		return `${poBoxNumber}${buildingName}${subBuildingNumber}${buildingNumber}${street}`
	}

	/**
	 * Get geocoding address text.
	 */
	geocodingAddress(): string {
		const buildingNumber = this.buildingNumber.map(bn => ` ${bn}`).getOrElse("");
		const streetStr = this.street.map(s => `${s}${buildingNumber},`).getOrElse("");
		const cityStr = this.city.map(s => `${s},`).getOrElse("");
		return `${streetStr}${cityStr}${this.postcode},${this.country}`;
	}
}
